import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listActiveStudents, listFrequencies } from "../services/student";
import {
  amortizationPay,
  downloadAmortizationReceipt,
  getOverduePayments,
  listStudentExtraconfigs,
} from "../services/payment";
import { levelKey } from "../utils/classesLabels";
import { getSucursalInfo, getUserInfo } from "../services/auth";
import { listPaymentTypes } from "../services/paymentTypes";
import { buildPropinaRows, formatCurrencyMZN, monthName, PAYMENT_METHODS } from "../utils/paymentHelpers";

const currentYear = new Date().getFullYear();

const AmortizacaoLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { studentId } = useParams();

  const [registration, setRegistration] = useState(location.state?.registration || null);
  const [loadingFallback, setLoadingFallback] = useState(!location.state?.registration);
  const [notFound, setNotFound] = useState(false);

  const [overduePayments, setOverduePayments] = useState([]);
  const [loadingOverdue, setLoadingOverdue] = useState(true);

  const overdueAmount = parseFloat(registration?.overdueAmount || 0);
  const [totalToPay, setTotalToPay] = useState(overdueAmount);
  const [calculatedTotal, setCalculatedTotal] = useState(overdueAmount);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [forgiveFine, setForgiveFine] = useState(false);
  const [fine, setFine] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [amortization, setAmortization] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState(PAYMENT_METHODS);

  // Tipos de Pagamento por sucursal (SGE_PAYMENT_TYPES) - PAYMENT_METHODS só
  // é usado como reserva se a chamada falhar ou a sucursal não tiver tipos.
  useEffect(() => {
    listPaymentTypes()
      .then((resp) => {
        const names = (resp.data || []).map((pt) => pt.name).filter(Boolean);
        if (names.length) setPaymentMethods(names);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (registration) return;
    Promise.all([listActiveStudents(currentYear), listStudentExtraconfigs(), listFrequencies()])
      .then(([regResp, extraResp, freqResp]) => {
        const extras = (extraResp.data || []).filter((item) => Number(item.year) === Number(currentYear));
        const rows = buildPropinaRows(regResp.data || [], extras, freqResp.data || [], currentYear);
        const found = rows.find((r) => String(r.studentId) === String(studentId));
        if (found) setRegistration(found);
        else setNotFound(true);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoadingFallback(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!registration) return;
    setTotalToPay(overdueAmount);
    setCalculatedTotal(overdueAmount);
    setFine(parseFloat(registration.fine) || 0);
    setLoadingOverdue(true);
    getOverduePayments(registration.registrationId)
      .then((resp) => {
        setOverduePayments(
          (resp.data || []).map((p) => ({
            key: p.id,
            year: p.year,
            month: monthName(p.month),
            total: p.total,
          })),
        );
      })
      .catch(() => setOverduePayments([]))
      .finally(() => setLoadingOverdue(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registration]);

  const handleChangeTotalToPay = (e) => {
    const value = e.target.value;
    const numericValue = parseFloat(value) || 0;
    if (numericValue < 0) {
      toast.error(t("propina.amortize.error.negative"));
      return;
    }
    if (numericValue > overdueAmount) {
      toast.error(t("propina.amortize.error.exceeds", { amount: formatCurrencyMZN(overdueAmount) }));
      return;
    }
    setTotalToPay(value);
    setCalculatedTotal(overdueAmount - numericValue);
  };

  const toggleForgiveFine = (checked) => {
    setForgiveFine(checked);
    if (checked) {
      const iva = getSucursalInfo()?.ivaPercentage || 0;
      const monthly = parseInt(registration.currentMonthlyPayment, 10) || 0;
      setFine(0);
      setTotalToPay(monthly + (monthly * iva) / 100);
    } else {
      setFine(parseFloat(registration.fine) || 0);
    }
  };

  const handleConfirm = async () => {
    const numericValue = parseFloat(totalToPay);
    if (!paymentMethod) {
      toast.error(t("propina.amortize.error.method"));
      return;
    }
    if (!numericValue || numericValue <= 0) {
      toast.error(t("propina.amortize.error.value"));
      return;
    }
    setSubmitting(true);
    try {
      const isEntidade = paymentMethod === "ENTIDADE";
      const resp = await amortizationPay(
        {
          amount: totalToPay,
          paymentMethod: isEntidade ? "ENTIDADE E REFERENCIA" : paymentMethod,
          updatedBy: getUserInfo()?.id,
          registrationId: registration.registrationId,
          ...(isEntidade ? { sucursal: getSucursalInfo() } : {}),
        },
        { entidade: isEntidade },
      );
      if (resp.data && resp.data.code === 201) {
        setAmortization(resp.data.amortization);
        setSuccess(true);
      } else {
        toast.error(resp.data?.message || t("propina.error.generic"));
      }
    } catch (err) {
      toast.error(t("propina.error.generic"));
    } finally {
      setSubmitting(false);
    }
  };

  const backToPayments = () => navigate("/pagamentos/propinas");

  const handlePrintReceipt = async () => {
    try {
      const lastPaymentId = registration.payments?.[registration.payments.length - 1]?.id;
      const resp = await downloadAmortizationReceipt(amortization?.id, lastPaymentId);
      if (resp.data?.pdf) {
        navigate("/documentos/pdf", {
          state: {
            url: resp.data.pdf,
            title: t("propina.documento.amortization", { name: registration.name }),
            filename: `factura-${registration.name}.pdf`,
          },
        });
      } else {
        toast.error(t("propina.error.print"));
      }
    } catch (err) {
      toast.error(t("propina.error.print"));
    }
  };

  if (loadingFallback) {
    return (
      <div className='row gy-4'>
        <div className='col-12 text-center text-secondary-light py-64'>{t("estudante.list.loading")}</div>
      </div>
    );
  }

  if (notFound || !registration) {
    return (
      <div className='row gy-4'>
        <div className='col-12'>
          <div className='card radius-12'>
            <div className='card-body text-center py-64'>
              <p className='text-secondary-light mb-16'>{t("propina.confirm.notFound")}</p>
              <button type='button' className='btn btn-primary-600' onClick={backToPayments}>
                <i className='ri-arrow-left-line' /> {t("propina.action.backToPayments")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='row gy-4'>
      <ToastContainer />
      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            {!success ? (
              <>
                <div className='alert alert-info d-flex flex-column mb-24'>
                  <strong>{t("propina.confirm.title")}</strong>
                  <span className='text-sm'>{t("propina.confirm.subtitle")}</span>
                </div>

                <h6 className='mb-16'>{registration.name}</h6>
                <div className='table-responsive mb-24'>
                  <table className='table table-bordered table-sm mb-0 bg-base'>
                    <tbody>
                      <tr>
                        <th className='w-25 bg-neutral-50'>{t(levelKey("propina.col.level"))}</th>
                        <td>{registration.level || "-"}</td>
                        <th className='w-25 bg-neutral-50'>{t("propina.col.enrolledAt")}</th>
                        <td>
                          {registration.createdAt
                            ? new Date(registration.createdAt).toLocaleDateString("pt-PT")
                            : "-"}
                        </td>
                      </tr>
                      <tr>
                        <th className='bg-neutral-50'>{t("propina.detail.fine")}</th>
                        <td>{formatCurrencyMZN(fine)}</td>
                        <th className='bg-neutral-50'>{t("propina.detail.discount")}</th>
                        <td>0%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className='p-24 radius-8 text-center mb-24 bg-danger-focus border border-danger-100'>
                  <span className='text-secondary-light text-sm d-block mb-8'>
                    {t("propina.amortize.totalDebt")}
                  </span>
                  <strong className='text-danger-main' style={{ fontSize: 28 }}>
                    {formatCurrencyMZN(calculatedTotal)}
                  </strong>
                </div>

                <h6 className='text-md mb-12'>{t("propina.amortize.overdueTitle")}</h6>
                <div className='table-responsive mb-24'>
                  <table className='table table-bordered table-sm mb-0 bg-base'>
                    <thead>
                      <tr>
                        <th className='bg-neutral-50'>{t("propina.col.year")}</th>
                        <th className='bg-neutral-50'>{t("propina.col.month")}</th>
                        <th className='bg-neutral-50'>{t("propina.col.total")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingOverdue && (
                        <tr>
                          <td colSpan={3} className='text-center text-secondary-light py-16'>
                            {t("estudante.list.loading")}
                          </td>
                        </tr>
                      )}
                      {!loadingOverdue && overduePayments.length === 0 && (
                        <tr>
                          <td colSpan={3} className='text-center text-secondary-light py-16'>
                            {t("propina.amortize.overdueEmpty")}
                          </td>
                        </tr>
                      )}
                      {overduePayments.map((p) => (
                        <tr key={p.key}>
                          <td>{p.year}</td>
                          <td>{p.month}</td>
                          <td>{formatCurrencyMZN(p.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className='row gy-3'>
                  <div className='col-sm-6'>
                    <label className='form-label'>{t("propina.amortize.amountToPay")} <span className='required-asterisk'>*</span></label>
                    <div className="icon-field">
                      <span className="icon">
                        <Icon icon="solar:dollar-minimalistic-outline" />
                      </span>
                      <input
                        type='number'
                        min='0'
                        step='0.01'
                        className='form-control'
                        value={totalToPay}
                        onChange={handleChangeTotalToPay}
                      />
                    </div>
                  </div>
                  <div className='col-sm-6'>
                    <label className='form-label'>{t("propina.field.paymentMethod")} <span className='required-asterisk'>*</span></label>
                    <select
                      className='form-control form-select'
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                      <option value=''>{t("estudante.select.placeholder")}</option>
                      {paymentMethods.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                      <option value='ENTIDADE'>{t("propina.amortize.paymentMethod.entidade")}</option>
                    </select>
                  </div>
                  <div className='col-12'>
                    <div className='form-check'>
                      <input
                        id='amortizeForgiveFine'
                        type='checkbox'
                        className='form-check-input'
                        checked={forgiveFine}
                        onChange={(e) => toggleForgiveFine(e.target.checked)}
                      />
                      <label htmlFor='amortizeForgiveFine' className='form-check-label'>
                        {t("propina.action.removeFine")}
                      </label>
                    </div>
                  </div>
                </div>

                <div className='d-flex align-items-center justify-content-end gap-8 mt-24'>
                  <button type='button' className='btn btn-neutral-500 border-neutral-100 px-32' onClick={backToPayments}>
                    {t("global.cancel")}
                  </button>
                  <button
                    type='button'
                    className='btn btn-primary-600 px-32'
                    onClick={handleConfirm}
                    disabled={submitting || !paymentMethod || !totalToPay || parseFloat(totalToPay) <= 0}
                  >
                    {submitting ? (
                      "..."
                    ) : (
                      <>
                        <i className='ri-check-line' /> {t("propina.amortize.confirm")}
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <div className='text-center py-40'>
                <span className='w-64-px h-64-px bg-success-focus text-success-main rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
                  <i className='ri-check-line' />
                </span>
                <h6 className='text-md text-neutral-600'>{t("propina.amortize.success.title")}</h6>
                <p className='text-neutral-400 text-sm mb-24'>
                  {t("propina.amortize.success.subtitle", { value: formatCurrencyMZN(totalToPay) })}
                </p>
                <div className='d-flex align-items-center justify-content-center gap-8'>
                  <button type='button' className='btn btn-outline-primary-600 px-32' onClick={handlePrintReceipt}>
                    {t("propina.action.invoice")}
                  </button>
                  <button type='button' className='btn btn-primary-600 px-32' onClick={backToPayments}>
                    <i className='ri-arrow-left-line' /> {t("propina.action.backToPayments")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmortizacaoLayer;
